import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Send, UserPlus, Trash2, Clock, CheckCircle2, XCircle } from "lucide-react";
import { EmptyState } from "@/components/common";

const money = (v, c) =>
  `${Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${c}`;

const STATUS = {
  processing: { icon: Clock, cls: "text-amber-600" },
  paid: { icon: CheckCircle2, cls: "text-emerald-600" },
  failed: { icon: XCircle, cls: "text-red-600" },
};

const emptyBen = { nickname: "", account_holder: "", currency: "GBP", country: "GB",
  iban: "", account_number: "", routing_code: "", swift: "", bank_name: "" };

/** Send money out to a client's own bank account over the same local rails. */
export const Payouts = ({ balances, onSent }) => {
  const [rails, setRails] = useState(null);
  const [bens, setBens] = useState([]);
  const [list, setList] = useState([]);
  const [form, setForm] = useState(emptyBen);
  const [addOpen, setAddOpen] = useState(false);
  const [sendFor, setSendFor] = useState(null);
  const [amount, setAmount] = useState("500");
  const [fromCcy, setFromCcy] = useState("USD");
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [r, b, l] = await Promise.all([
      api.get("/payout/rails"), api.get("/payout/beneficiaries"), api.get("/payout/list"),
    ]);
    setRails(r.data); setBens(b.data); setList(l.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!sendFor || !Number(amount)) { setQuote(null); return undefined; }
    let alive = true;
    const t = setTimeout(() => {
      api.post("/payout/quote", { beneficiary_id: sendFor.id, amount: Number(amount), from_currency: fromCcy })
        .then(({ data }) => { if (alive) setQuote(data); })
        .catch(() => { if (alive) setQuote(null); });
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [sendFor, amount, fromCcy]);

  const addBen = async () => {
    setBusy(true);
    try {
      await api.post("/payout/beneficiaries", form);
      toast.success("Beneficiary saved");
      setForm(emptyBen); setAddOpen(false); load();
    } catch (e) { toast.error("Couldn't save", { description: e?.response?.data?.detail }); }
    finally { setBusy(false); }
  };

  const removeBen = async (id) => {
    await api.delete(`/payout/beneficiaries/${id}`);
    toast.info("Beneficiary removed"); load();
  };

  const sendMoney = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payout/send", {
        beneficiary_id: sendFor.id, amount: Number(amount), from_currency: fromCcy });
      toast.success("Transfer on its way",
        { description: `${money(data.receive_amount, data.receive_currency)} to ${data.beneficiary}` });
      setSendFor(null); onSent?.();
      load();
      setTimeout(load, 8000);
    } catch (e) { toast.error("Transfer failed", { description: e?.response?.data?.detail }); }
    finally { setBusy(false); }
  };

  if (!rails) return null;
  const holdCcys = Object.entries(balances || {}).filter(([, v]) => v > 0).map(([c]) => c);
  const walletCcys = holdCcys.length ? holdCcys : rails.wallet_currencies;

  return (
    <Card className="p-5 shadow-premium mt-4 sm:mt-5" data-testid="payouts-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-sm font-medium">
            <Send className="h-4 w-4" />Send money out
          </div>
          <p className="text-[11px] text-muted-foreground mt-1 max-w-xl leading-relaxed">
            Pay your own account or anyone else over the same local rails we collect on —
            {rails.fee_pct}% fee (min ${rails.fee_min_usd}), {rails.fx_spread_pct}% spread when we convert.
            Routed automatically through Airwallex, Currencycloud or Nium.
          </p>
        </div>
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogTrigger asChild>
            <Button variant="secondary" data-testid="payout-add-beneficiary">
              <UserPlus className="h-4 w-4 me-1.5" />Add beneficiary
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="payout-beneficiary-dialog">
            <DialogHeader><DialogTitle>Add a beneficiary</DialogTitle></DialogHeader>
            <div className="grid gap-3 sm:grid-cols-2">
              <div><Label className="text-xs">Nickname</Label>
                <Input value={form.nickname} onChange={(e) => setForm({ ...form, nickname: e.target.value })}
                  placeholder="My HSBC account" data-testid="ben-nickname" /></div>
              <div><Label className="text-xs">Account holder</Label>
                <Input value={form.account_holder} onChange={(e) => setForm({ ...form, account_holder: e.target.value })}
                  data-testid="ben-holder" /></div>
              <div><Label className="text-xs">Currency</Label>
                <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                  <SelectTrigger data-testid="ben-currency"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    {rails.currencies.map((c) => (
                      <SelectItem key={c.currency} value={c.currency}>{c.currency} · {c.rail}</SelectItem>
                    ))}
                  </SelectContent>
                </Select></div>
              <div><Label className="text-xs">Country (2 letters)</Label>
                <Input value={form.country} maxLength={2}
                  onChange={(e) => setForm({ ...form, country: e.target.value.toUpperCase() })}
                  data-testid="ben-country" /></div>
              <div><Label className="text-xs">IBAN</Label>
                <Input value={form.iban} onChange={(e) => setForm({ ...form, iban: e.target.value })}
                  data-testid="ben-iban" /></div>
              <div><Label className="text-xs">Account number</Label>
                <Input value={form.account_number} onChange={(e) => setForm({ ...form, account_number: e.target.value })}
                  data-testid="ben-account" /></div>
              <div><Label className="text-xs">Routing / sort code</Label>
                <Input value={form.routing_code} onChange={(e) => setForm({ ...form, routing_code: e.target.value })}
                  data-testid="ben-routing" /></div>
              <div><Label className="text-xs">Bank name</Label>
                <Input value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })}
                  data-testid="ben-bank" /></div>
            </div>
            <DialogFooter>
              <Button onClick={addBen} disabled={busy || !form.nickname || !form.account_holder}
                data-testid="ben-save">Save beneficiary</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {!bens.length ? (
        <EmptyState compact testid="payout-beneficiaries-empty" title="No beneficiaries yet"
          hint={`Add one and you can send in ${rails.currencies.length} currencies.`} />
      ) : (
        <div className="mt-4 grid gap-2.5 sm:grid-cols-2" data-testid="payout-beneficiaries">
          {bens.map((b) => (
            <div key={b.id} className="rounded-xl border p-3.5" data-testid={`beneficiary-${b.nickname.replace(/\s+/g, "-")}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate">{b.nickname}</div>
                  <div className="text-[11px] text-muted-foreground truncate">
                    {b.account_holder} · {b.currency} · {b.rail}
                  </div>
                  <div className="text-[11px] text-muted-foreground numeric truncate">
                    {b.iban || b.account_number}
                  </div>
                </div>
                <Badge variant="secondary" className="text-[10px] shrink-0">{b.provider_label}</Badge>
              </div>
              <div className="mt-2.5 flex items-center gap-1.5">
                <Button size="sm" className="h-8" onClick={() => { setSendFor(b); setQuote(null); }}
                  data-testid={`payout-send-${b.currency}`}>
                  <Send className="h-3.5 w-3.5 me-1" />Send
                </Button>
                <Button size="sm" variant="ghost" className="h-8" onClick={() => removeBen(b.id)}
                  data-testid={`payout-delete-${b.currency}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!sendFor} onOpenChange={(o) => !o && setSendFor(null)}>
        <DialogContent data-testid="payout-send-dialog">
          <DialogHeader><DialogTitle>Send to {sendFor?.nickname}</DialogTitle></DialogHeader>
          <div className="flex items-end gap-2">
            <div className="w-28"><Label className="text-xs">From</Label>
              <Select value={fromCcy} onValueChange={setFromCcy}>
                <SelectTrigger data-testid="payout-from-ccy"><SelectValue /></SelectTrigger>
                <SelectContent>{walletCcys.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="flex-1"><Label className="text-xs">Amount to send</Label>
              <Input value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="payout-amount" /></div>
          </div>
          {quote && (
            <div className="rounded-xl border p-3.5 text-xs space-y-1.5" data-testid="payout-quote">
              <div className="flex justify-between"><span className="text-muted-foreground">Leaves your wallet</span>
                <span className="numeric font-medium">{money(quote.debit_amount, quote.debit_currency)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Fee ({quote.fee_pct}%)</span>
                <span className="numeric">{money(quote.fee, quote.fee_currency)}</span></div>
              {!!quote.fx_spread_pct && (
                <div className="flex justify-between"><span className="text-muted-foreground">FX spread</span>
                  <span className="numeric">{quote.fx_spread_pct}%</span></div>
              )}
              <div className="flex justify-between"><span className="text-muted-foreground">Rate</span>
                <span className="numeric">1 {quote.debit_currency} = {quote.rate} {quote.receive_currency}</span></div>
              <div className="flex justify-between border-t pt-1.5">
                <span className="font-medium">They receive</span>
                <span className="numeric font-semibold text-emerald-600" data-testid="payout-quote-receive">
                  {money(quote.receive_amount, quote.receive_currency)}
                </span></div>
              <div className="text-[11px] text-muted-foreground pt-0.5">
                {quote.rail} · {quote.provider} · arrives {quote.arrives}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={sendMoney} disabled={busy || !quote} data-testid="payout-confirm">
              Send {quote ? money(quote.debit_amount, quote.debit_currency) : ""}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {!!list.length && (
        <div className="mt-4" data-testid="payout-history">
          <div className="text-xs font-medium mb-2">Money out</div>
          <div className="divide-y">
            {list.slice(0, 6).map((p) => {
              const s = STATUS[p.status] || STATUS.processing;
              const Icon = s.icon;
              return (
                <div key={p.id} className="flex items-center justify-between py-2 text-xs">
                  <div className="min-w-0">
                    <div className="font-medium truncate">{p.beneficiary} · {p.reference}</div>
                    <div className="text-muted-foreground truncate">{p.rail} · {p.provider_label}</div>
                  </div>
                  <div className="text-end shrink-0 ps-3">
                    <div className="numeric font-medium">{money(p.receive_amount, p.receive_currency)}</div>
                    <div className={`inline-flex items-center gap-1 ${s.cls}`}>
                      <Icon className="h-3 w-3" />{p.status}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </Card>
  );
};
